/* eslint-disable no-param-reassign */
import { produce } from 'immer';

import {
  CustomColumnHeader,
  CustomColumnItem,
  ErrorState,
  EventRequest,
  getAdderColumnType,
  getColumnHeaderDataTypes,
  GlobalLotsUiConfigKeys,
  ID,
  LineItem,
  LineItemChanges,
  LineItemsState,
  Lot,
  LotChanges,
  LotPagination,
} from 'types';
import {
  applyChanges,
  cleanUpChanges,
  createDefaultLot,
  getDefaultLineItemForLot,
  getTotalLineItemCount,
  isCustomColumnHeaderChanged,
  isLotChanged,
  matchesNew,
  mergeLots,
  updateChanges,
} from '@components/bid_request/details/utils';
import { NEGOTIATION_STARTING_PRICE_COLUMN_KEY, PRICE_COLUMN_KEY } from '@components/bid_request/details/constants';
import { REQUEST_TYPE_PRICE_UPDATE } from '@components/bid_request/RequestConstants';
import { withholdNsbCells } from '@components/bid_request/details/LineItemsTable/SmartBaselineBenchmarkRenderer/nsbApplyStore';
import { markUploadArrived } from '@components/bid_request/details/LineItemsTable/ItemIntelLinkRenderer/matchProgressStore';
import {
  cleanUpLotChanges,
  DEFAULT_LOT_PAGINATION,
  dropPendingColumnCells,
  idErrorsStore,
  mergeShellLots,
  remergeLot,
  fillPendingColumnCells,
} from '@features/buyer-line-items-pagination';
import { resetNsbLineItemState } from '@components/bid_request/details/LineItemsTable/SmartBaselineBenchmarkRenderer/nsbEventReset';
import { resetPickedResolutions } from '@components/bid_request/details/LineItemsTable/SmartBaselineBenchmarkRenderer/nsbResolvedItemBridge';
import { addErrorState, removeErrorState } from '@components/bid_request/details/utils/validations';
import { Global } from '@utils/common';
import { globalLotsUiConfigKeys } from '@components/bid_request/details/StickyHeader/EventConfigurationMenu/utils/prepareUiConfigChanges';

import {
  setDefaultsForCustomColumnDataTypes,
  ensureLotStructure,
  processLineItemChanges,
  processCustomColumnItemChanges,
  createEventState,
  defaultChanges,
  getOriginalHeader,
  getOriginalLineItem,
  getOriginalLot,
  processImportConfirmationResults,
} from './utils';
import { markUserProvidedBaselines } from './utils/markUserProvidedBaselines';
import {
  ACCEPTED_CURRENCIES_CHANGE,
  ADD_BLANK_LINE_ITEM,
  ADD_CUSTOM_COLUMN,
  ADD_LOT,
  APPLY_BASELINE_CHANGES,
  APPLY_CHANGES_TO_CUSTOM_COLUMN,
  COLLAPSE_LOT,
  CURRENCY_CHANGE,
  DELETE_LINE_ITEM,
  DELETE_LOT,
  DISCARD_CHANGES,
  ERRORS_CHANGE,
  EVENT_CHANGES,
  EVENT_INCUMBENT_CHANGE,
  EVENT_SHELL_REFRESHED,
  EXPAND_ALL,
  EXPAND_LOT,
  FETCH_BASELINE_IN_PROGRESS,
  FETCH_BASELINE_IN_PROGRESS_END,
  FETCH_BASELINE_PRICES,
  FETCH_LINE_ITEMS,
  FINISH_PROGRESS,
  LINE_ITEM_CHANGE,
  LINE_ITEMS_IMPORT_CANCEL,
  LINE_ITEMS_IMPORT_CONFIRM_ADD,
  LINE_ITEMS_IMPORT_CONFIRM_REPLACE_ALL,
  LINE_ITEMS_IMPORT_ERROR,
  LINE_ITEMS_IMPORT_SAVE_FINISHED,
  LINE_ITEMS_IMPORT_START,
  LINE_ITEMS_IMPORT_SUCCESS,
  LINE_ITEMS_UPLOAD,
  LINE_ITEMS_UPLOAD_APPLY_COLUMN_LIMIT,
  LINE_ITEMS_UPLOAD_CANCEL,
  LINE_ITEMS_UPLOAD_COLUMN_WARNING,
  LOT_CARD_HEADER_CHANGES,
  LOT_PAGE_CHANGE,
  LOT_PAGE_LOADED,
  REFRESH_VALIDATION,
  REFRESH_VALIDATION_DONE,
  REMOVE_COLUMN,
  RESET_BASELINE_STATE,
  SHOULD_REFRESH,
  SHOULD_RERENDER_LINE_ITEM_DATA,
  START_FETCH,
  SYNC_ADDER_COLUMNS,
  START_PROGRESS,
  UPDATE_UI_CONFIG,
  UPDATE_ZERO_PRICED_LINE_ITEMS,
} from './constants';
import {
  AcceptedCurrenciesChangeAction,
  AddBlankLineItem,
  AddCustomColumnAction,
  AddLotAction,
  ApplyBaselineChangesAction,
  ApplyChangesToCustomColumnAction,
  CollapseLotAction,
  CurrencyChangeAction,
  DeleteLineItemAction,
  DeleteLotAction,
  DiscardChangesAction,
  ErrorsChangesAction,
  EventChangesAction,
  EventIncumbentChangeAction,
  EventShellRefreshedAction,
  ExpandAllAction,
  ExpandLotAction,
  FetchLineItemsAction,
  FetchBaselinePricesProgressEnd,
  FinishProgressAction,
  LineItemChangeAction,
  LineItemsImportStartAction,
  LineItemsAction,
  LineItemsImportCancelAction,
  LineItemsImportConfirmAddAction,
  LineItemsImportConfirmReplaceAllAction,
  LineItemsImportErrorAction,
  LineItemsImportSaveFinishedAction,
  LineItemsImportSuccessAction,
  LineItemsUploadAction,
  LineItemsUploadApplyColumnLimitAction,
  LineItemsUploadCancelAction,
  LineItemsUploadColumnLimitAction,
  LotCardHeaderChangesAction,
  LotPageChangeAction,
  LotPageLoadedAction,
  RefreshValidationAction,
  RefreshValidationDoneAction,
  RemoveColumnAction,
  ShouldRefreshAction,
  ShouldRerenderLineItemDataAction,
  StartFetchAction,
  SyncAdderColumnsAction,
  StartProgressAction,
  UpdateUiConfigAction,
  UpdateUpdateZeroPricedLineItemCountAction,
} from './action_types';
import { applyDescriptiveColumnLimits } from './utils/applyDescriptiveColumnLimits';
import { defaultBaselinePriceState, defaultImportState } from './defaultLineItemsState';

type Action<T extends LineItemsAction> = (
  _state: LineItemsState, _action?: T, _request?: EventRequest,
) => LineItemsState;

const getLotPagination = (state: LineItemsState, lotId: ID): LotPagination => state.lotsPagination[lotId] ?? DEFAULT_LOT_PAGINATION;

const settleLotEdit = (state: LineItemsState, lotId: ID, { remerge }: { remerge: boolean }): LineItemsState => {
  const changes = cleanUpLotChanges(state.changes, lotId);
  const lots = remerge
    ? remergeLot({ changes, lotId, lots: state.lots, originalLots: state.originalLots })
    : state.lots;

  return { ...state, changes, lots };
};

export const handleAddBlankLineItem: Action<AddBlankLineItem> = (
  state: LineItemsState,
  action?: AddBlankLineItem,
): LineItemsState => {
  if (!action?.payload) return state;
  const { lot, name } = action.payload;
  const lotId = lot.id;

  // find the highest sno in the lot and add 1
  const currentLot = state.lots?.find(item => item.id === lotId) ?? lot;
  const existingSNos = currentLot.line_items.map(l => l.sno);
  const newSno = Math.max(0, ...existingSNos) + 1;

  // create a new line item with the new sno
  const newLineItem = getDefaultLineItemForLot({
    headers: lot.custom_column_headers,
    lotId,
    sno: newSno,
  });

  newLineItem.negotiation_starting_price = Global.line_items.defaults.price;
  if (name?.trim()) newLineItem.name = name;

  return produce(state, (draft: LineItemsState): void => {
    draft.changes = draft.changes || ({} as Record<ID, LotChanges>);
    draft.changes[lotId] = draft.changes[lotId] || ({ id: lotId } as LotChanges);
    draft.changes[lotId].line_items = draft.changes[lotId].line_items
      || ({} as Record<ID, LineItemChanges>);
    draft.changes[lotId].line_items[newLineItem.id] = newLineItem;
    draft.lots = applyChanges({ lotChanges: draft.changes, lots: draft.originalLots });
  });
};

export const handleAddCustomColumn: Action<AddCustomColumnAction> = (
  state: LineItemsState,
  action?: AddCustomColumnAction,
): LineItemsState => {
  if (!action?.payload) return state;
  const { lotId, column } = action.payload;

  return produce(state, (draft: LineItemsState): void => {
    draft.changes = draft.changes || ({} as Record<ID, LotChanges>);
    draft.changes[lotId] = draft.changes[lotId] || ({ id: lotId } as LotChanges);
    draft.changes[lotId].custom_column_headers = draft.changes[lotId].custom_column_headers
      || ({} as Record<ID, CustomColumnHeader>);

    // Adder columns must use a column_order_index that is unique across ALL lots,
    // not just the source lot. Event-wide adders are propagated to every lot at the
    // same index, so a per-lot index can collide with another lot's lot-scoped adder
    // at the same position. The reducer recomputes a globally safe index here so the
    // drawer save sends a non-colliding value.
    const isAdder = column.column_type === getAdderColumnType();

    if (isAdder && draft.lots?.length) {
      const globalMaxIndex = draft.lots.reduce((max, l) => {
        const lotMax = (l.custom_column_headers ?? []).reduce(
          (m, h) => (typeof h.column_order_index === 'number' && h.column_order_index > m ? h.column_order_index : m),
          -1,
        );

        return lotMax > max ? lotMax : max;
      }, -1);

      column.column_order_index = globalMaxIndex + 1;
    }

    draft.changes[lotId].custom_column_headers[column.id] = column;

    // Create defaults for custom column data types for newly added custom columns
    draft.changes = setDefaultsForCustomColumnDataTypes({
      changes: draft.changes,
      dataType: column.data_type,
      headerId: column.id,
      isDataTypeChanged: true, // New column, so data type is "changed" from non-existent
      lotId,
      lots: draft.originalLots,
    });

    draft.lots = applyChanges({ lotChanges: draft.changes, lots: draft.originalLots });
  });
};

export const handleAddLot: Action<AddLotAction> = (
  state: LineItemsState,
  action?: AddLotAction,
): LineItemsState => {
  if (!action?.payload) return state;
  const { t } = action.payload;
  const newLot = createDefaultLot({
    isPriceUpdate: state.event.requestType === REQUEST_TYPE_PRICE_UPDATE,
    lotChanges: state.changes,
    lots: state.originalLots,
    t,
  }) as LotChanges;

  return produce(state, (draft: LineItemsState): void => {
    draft.changes = draft.changes || ({} as Record<ID, LotChanges>);
    draft.changes[newLot.id] = newLot;
    draft.lots = applyChanges({ lotChanges: draft.changes, lots: draft.originalLots });
  });
};

export const handleApplyChangesToCustomColumn: Action<ApplyChangesToCustomColumnAction> = (
  state: LineItemsState,
  action?: ApplyChangesToCustomColumnAction,
): LineItemsState => {
  if (!action?.payload) return state;
  const { lotId, headerId, updates } = action.payload;

  return produce(state, (draft: LineItemsState): void => {
    const originalHeader = getOriginalHeader({ headerId, lotId, lots: draft.originalLots });
    draft.changes = draft.changes || ({} as Record<ID, LotChanges>);
    draft.changes[lotId] = draft.changes[lotId] || ({ id: lotId } as LotChanges);
    draft.changes[lotId].custom_column_headers = draft.changes[lotId].custom_column_headers
      || ({} as Record<ID, CustomColumnHeader>);
    const lastDataType = draft.changes[lotId].custom_column_headers[headerId]?.data_type ||
      originalHeader?.data_type || getColumnHeaderDataTypes().TEXT;
    const isDataTypeChanged = Boolean(updates.data_type && updates.data_type !== lastDataType);
    const touchedFields = Object.keys({ ...draft.changes[lotId].custom_column_headers[headerId], ...updates })
      .filter(key => key !== 'id' && key !== 'lot_id') as (keyof CustomColumnHeader)[];
    draft.changes[lotId].custom_column_headers[headerId] = updateChanges({
      changes: draft.changes[lotId].custom_column_headers[headerId] ||
        ({ id: headerId, lot_id: lotId } as CustomColumnHeader),
      original: originalHeader,
      updates,
    }) as CustomColumnHeader;
    // IMPORTANT: This will check if the header has changed, but it won't detect changes
    // if it's NOT listed on `details/utils/checkForChanges#customColumnHeadersFields`
    const isChanged = isCustomColumnHeaderChanged({ headerId, lotId, state: draft });

    if (!isChanged && draft.event.paginationEnabled && originalHeader) {
      // Flag on, a type, name or visibility changed back, or an edit with no net change, keeps (or gets)
      // its pending header with the saved value of each touched field written back, so the cells the walk
      // wrote stay walk cells. updateChanges strips fields equal to the saved ones.
      const savedValues = Object.fromEntries(touchedFields.map(key => [key, originalHeader[key]]));
      draft.changes[lotId].custom_column_headers[headerId] = {
        ...(draft.changes[lotId].custom_column_headers[headerId] ?? { id: headerId, lot_id: lotId }),
        ...savedValues,
      } as CustomColumnHeader;
    } else if (!isChanged) {
      // If there are no changes, remove the header
      delete draft.changes[lotId].custom_column_headers[headerId];
    }
    // Create a default for column data_types
    draft.changes = setDefaultsForCustomColumnDataTypes({
      changes: draft.changes,
      dataType: updates.data_type || lastDataType,
      headerId,
      isDataTypeChanged,
      lotId,
      lots: draft.originalLots,
    });

    if (draft.event.paginationEnabled && originalHeader && updates.is_supplier_visible !== undefined) {
      // Flag on, a blank cell the walk wrote in a row with no saved cell follows the header's visibility,
      // so a visibility changed back leaves it a walk cell.
      const visibility = draft.changes[lotId].custom_column_headers?.[headerId]?.is_supplier_visible
        ?? originalHeader.is_supplier_visible;
      Object.values(draft.changes[lotId].line_items ?? {}).forEach(rowChanges => {
        const savedItem = getOriginalLineItem({ lineItemId: rowChanges.id, lotId, lots: draft.originalLots });
        const hasSavedCell = savedItem?.custom_column_items
          ?.some(item => String(item.custom_column_header_id) === String(headerId));
        Object.values(rowChanges.custom_column_items ?? {}).forEach(cell => {
          const isBlank = cell?.display_text == null || cell.display_text === '';
          if (cell && !hasSavedCell && isBlank && String(cell.custom_column_header_id) === String(headerId)) {
            cell.is_supplier_visible = visibility;
          }
        });
      });
    }

    draft.changes = cleanUpChanges(draft.changes);
    draft.lots = applyChanges({ lotChanges: draft.changes, lots: draft.originalLots });
  });
};

export const handleBaselineFetchProgress = (state: LineItemsState): LineItemsState => (
  produce(state, (draft: LineItemsState): void => {
    draft.baselineState.inProgress = true;
    draft.isInProgress = true;
    draft.isLoading = true;
  })
);

export const handleFetchBaselinesAfterSave = (state: LineItemsState): LineItemsState => (
  produce(state, (draft: LineItemsState): void => {
    draft.baselineState.fetchBaselinesAfterSave = true;
  })
);

export const handleBaselineFetchProgressEnd = (state: LineItemsState, action: FetchBaselinePricesProgressEnd): LineItemsState => (
  produce(state, (draft: LineItemsState): void => {
    draft.baselineState.fetchBaselinesAfterSave = false;
    draft.baselineState.fetchBaselines = false;
    draft.baselineState.inProgress = false;
    draft.isLoading = false;
    draft.isInProgress = false;

    if (action?.payload?.showZeroBaselinesAlert) {
      draft.baselineState.showBaselinePriceUpdateAlert = true;
    }
  })
);

export const handleResetBaselineState = (state: LineItemsState): LineItemsState => (
  produce(state, (draft: LineItemsState): void => {
    draft.baselineState = { ...defaultBaselinePriceState };
  })
);

export const handleUpdateZeroPricedLineItemCount = (state: LineItemsState, action: UpdateUpdateZeroPricedLineItemCountAction) => {
  const { zeroPricedLineItemsCount } = action.payload;

  return (
    produce(state, (draft: LineItemsState): void => {
      draft.baselineState.zeroPricedLineItemsCount = zeroPricedLineItemsCount;
    })
  );
};

/**
 * Handles applying baseline price changes to line items and updating the state.
 *
 * @param {LineItemsState} state - The current state.
 * @param {ApplyBaselineChangesAction} action - The action containing baseline data.
 * @returns {LineItemsState} The new state with baseline prices applied to line items.
 */
export const handleApplyBaselineChanges: Action<ApplyBaselineChangesAction> = (
  state: LineItemsState,
  action?: ApplyBaselineChangesAction,
): LineItemsState => {
  if (!action?.payload?.baselineAppliedLineItems) return state;
  const { baselineAppliedLineItems } = action.payload;

  return produce(state, (draft: LineItemsState): void => {
    // Ensure changes object exists
    draft.changes = draft.changes || ({} as Record<ID, LotChanges>);
    draft.importState = draft.importState || ({} as Record<ID, LotChanges>);

    draft.baselineState.baselineCount = baselineAppliedLineItems.length;

    // Process each baseline item
    baselineAppliedLineItems.forEach(({ baseLinePrice, id, lotId }) => {
      const lotIdStr = lotId.toString();
      const lineItemIdStr = id.toString();

      // Initialize lot changes if not exists
      if (!draft.changes[lotIdStr]) {
        draft.changes[lotIdStr] = {
          id: lotId,
          line_items: {}
        } as LotChanges;
      }

      // Ensure line_items object exists
      if (!draft.changes[lotIdStr].line_items) {
        draft.changes[lotIdStr].line_items = {} as Record<ID, LineItemChanges>;
      }

      const originalLineItem = getOriginalLineItem({
        lineItemId: id,
        lotId,
        lots: draft.originalLots
      });

      // Update whichever of price and negotiation_starting_price are 0, or both.
      const updates: Record<string, string> = {};
      const priceStr = baseLinePrice.toString();

      if (Number(originalLineItem?.price) === 0) {
        updates[PRICE_COLUMN_KEY] = priceStr;
      }
      if (Number(originalLineItem?.negotiation_starting_price) === 0) {
        updates[NEGOTIATION_STARTING_PRICE_COLUMN_KEY] = priceStr;
      }

      draft.changes[lotIdStr].line_items[lineItemIdStr] = updateChanges({
        changes: draft.changes[lotIdStr].line_items[lineItemIdStr] ||
          ({ id, lot_id: lotId } as LineItemChanges),
        original: originalLineItem,
        updates,
      }) as LineItemChanges;
    });

    draft.changes = cleanUpChanges(draft.changes);

    draft.lots = applyChanges({ lotChanges: draft.changes, lots: draft.originalLots });

    draft.baselineState.fetchBaselinesAfterSave = false;
    draft.baselineState.fetchBaselines = false;
    draft.baselineState.inProgress = false;
    draft.isLoading = false;
    draft.isInProgress = false;
    draft.baselineState.baselinesApplied = true;
    draft.baselineState.showBaselinePriceUpdateAlert = true;
    draft.shouldRerenderLineItemData = true;
  });
};

export const handleAcceptedCurrenciesChange: Action<AcceptedCurrenciesChangeAction> = (
  state: LineItemsState,
  action?: AcceptedCurrenciesChangeAction,
): LineItemsState => {
  if (!action?.payload) return state;
  // NOTE: Maintaining interface with the backend key
  // eslint-disable-next-line camelcase
  const { bid_request_exchange_rates } = action.payload;

  return produce(state, (draft: LineItemsState): void => {
    draft.event = {
      ...draft.event,
      // eslint-disable-next-line camelcase
      bid_request_exchange_rates
    };
    draft.lots = applyChanges({ lotChanges: draft.changes, lots: draft.originalLots });
  });
};

export const handleCurrencyChange: Action<CurrencyChangeAction> = (
  state: LineItemsState,
  action?: CurrencyChangeAction,
): LineItemsState => {
  if (!action?.payload) return state;
  const { currency } = action.payload;

  return produce(state, (draft: LineItemsState): void => {
    draft.event = { ...draft.event, currency };
    draft.lots = applyChanges({ lotChanges: draft.changes, lots: draft.originalLots });
  });
};

export const handleCollapseLot: Action<CollapseLotAction> = (
  state: LineItemsState,
  action?: CollapseLotAction,
): LineItemsState => {
  if (!action?.payload) return state;
  const { lotId } = action.payload;

  return produce(state, (draft: LineItemsState): void => {
    draft.changes = draft.changes || ({} as Record<ID, LotChanges>);
    draft.changes[lotId] = draft.changes[lotId] || ({ id: lotId } as LotChanges);
    draft.changes[lotId] = {
      ...draft.changes[lotId],
      _collapsed: true,
    } as LotChanges;
    draft.lots = applyChanges({ lotChanges: draft.changes, lots: draft.originalLots });
  });
};

export const handleDeleteLineItem: Action<DeleteLineItemAction> = (
  state: LineItemsState,
  action?: DeleteLineItemAction,
): LineItemsState => {
  if (!action?.payload) return state;
  const { lotId, id: lineItemId } = action.payload;

  const marked = produce(state, (draft: LineItemsState): void => {
    draft.changes = draft.changes || ({} as Record<ID, LotChanges>);
    draft.changes[lotId] = draft.changes[lotId] || ({ id: lotId } as LotChanges);
    draft.changes[lotId].line_items = draft.changes[lotId].line_items
      || ({} as Record<ID, LineItemChanges>);
    if (matchesNew(lineItemId)) {
      delete draft.changes[lotId].line_items[lineItemId];
    } else {
      draft.changes[lotId].line_items[lineItemId] = {
        _destroy: true,
        id: lineItemId,
      } as LineItemChanges;
    }
  });
  const settled = marked.event.paginationEnabled
    ? settleLotEdit(marked, lotId, { remerge: true })
    : produce(marked, (draft: LineItemsState): void => {
      draft.changes = cleanUpChanges(draft.changes);
      draft.lots = applyChanges({ lotChanges: draft.changes, lots: draft.originalLots });
    });

  return produce(settled, (draft: LineItemsState): void => {

    if (draft.lotsErrors?.[lotId]) {
      delete draft.lotsErrors[lotId][lineItemId];
    }
    if (draft.event.paginationEnabled) idErrorsStore.clearRow(lotId, lineItemId);
  });
};

/**
 * Handles the deletion of a lot from the state.
 *
 * @param {LineItemsState} state - The current state.
 * @param {DeleteLotAction} action - The action to be handled.
 * @returns {LineItemsState} The new state with the lot deleted.
 */
export const handleDeleteLot: Action<DeleteLotAction> = (
  state: LineItemsState,
  action?: DeleteLotAction,
): LineItemsState => {
  if (!action?.payload) return state;
  const { lotId, t } = action.payload;
  return produce(state, (draft: LineItemsState): void => {
    draft.changes = draft.changes || ({} as Record<ID, LotChanges>);
    if (matchesNew(lotId)) {
      delete draft.changes[lotId];
    } else {
      draft.changes[lotId] = { _destroy: true, id: lotId } as LotChanges;
    }
    const lots = applyChanges({ lotChanges: draft.changes, lots: draft.originalLots });

    // If there are no lots, add an empty lot.
    if (!lots?.length) {
      const newLot = createDefaultLot({
        isPriceUpdate: draft.event.requestType === REQUEST_TYPE_PRICE_UPDATE,
        lotChanges: draft.changes,
        lots: draft.originalLots,
        t,
      }) as unknown as LotChanges;

      draft.changes[newLot.id] = newLot;
    }
    draft.changes = cleanUpChanges(draft.changes);
    draft.lots = applyChanges({ lotChanges: draft.changes, lots: draft.originalLots });
  });
};

/**
 * Provides a mechanism to force re-rendering of the table data. Useful for scenarios when we
 * have interactions where the table strucure may not have changed, but we need to force refresh
 * such as discarding changes for example.
 *
 * @param {LineItemsState} state - The current state.
 * @param {ShouldRerenderLineItemDataAction} action - The action to be handled.
 * @returns {LineItemsState} - the line item state with the force shouldRerenderLineItemData attribute set to true.
 */

export const handleRerenderTableData: Action<ShouldRerenderLineItemDataAction> = (
  state: LineItemsState,
  action?: ShouldRerenderLineItemDataAction,
): LineItemsState => {
  if (!action?.payload) return state;

  const { shouldRerenderLineItemData } = action.payload;

  return produce(state, (draft: LineItemsState): void => {
    draft.shouldRerenderLineItemData = shouldRerenderLineItemData;
  });
};

/**
 * Handles discarding changes and resetting the state to its original lots.
 *
 * @param {LineItemsState} state - The current state.
 * @param {DiscardChangesAction} action - The action to be handled.
 * @returns {LineItemsState} The new state with the original lots restored,
 * change count reset, and changes cleared.
 */
export const handleDiscardChanges: Action<DiscardChangesAction> = (
  state: LineItemsState,
  action?: DiscardChangesAction,
): LineItemsState => {
  if (!action?.payload) return state;
  const { t } = action.payload;

  return produce(state, (draft: LineItemsState): void => {
    const { originalCurrency, originalEventIncumbentId } = draft.event;
    draft.shouldRerenderLineItemData = true;
    draft.baselineState.showBaselinePriceUpdateAlert = false;

    draft.event = {
      ...draft.event,
      currency: originalCurrency,
      eventIncumbentId: originalEventIncumbentId,
    };
    draft.refreshValidation = true;
    draft.lotsErrors = {} as Record<ID, ErrorState>;
    if (draft.event.paginationEnabled) idErrorsStore.clearAll();
    draft.changes = defaultChanges({
      isPriceUpdate: draft.event.requestType === REQUEST_TYPE_PRICE_UPDATE,
      lots: draft.originalLots,
      t,
    });
    draft.lots = applyChanges({ lotChanges: draft.changes, lots: draft.originalLots });
  });
};

export const handleExpandAll: Action<ExpandAllAction> = (
  state: LineItemsState,
): LineItemsState => produce(state, (draft: LineItemsState): void => {
  draft.changes = draft.changes || ({} as Record<ID, LotChanges>);
  Object.keys(draft.changes).forEach((lotId): void => {
    draft.changes[lotId] = draft.changes[lotId] || ({ id: lotId } as LotChanges);
    draft.changes[lotId] = {
      ...draft.changes[lotId],
      _collapsed: false,
    } as LotChanges;
  });
  draft.lots = applyChanges({ lotChanges: draft.changes, lots: draft.originalLots });
});

export const handleExpandLot: Action<ExpandLotAction> = (
  state: LineItemsState,
  action?: ExpandLotAction,
): LineItemsState => {
  if (!action?.payload) return state;
  const { lotId } = action.payload;

  return produce(state, (draft: LineItemsState): void => {
    draft.changes = draft.changes || ({} as Record<ID, LotChanges>);
    draft.changes[lotId] = draft.changes[lotId] || ({ id: lotId } as LotChanges);
    draft.changes[lotId] = {
      ...draft.changes[lotId],
      _collapsed: false,
    } as LotChanges;
    draft.lots = applyChanges({ lotChanges: draft.changes, lots: draft.originalLots });
  });
};

/**
 * Handles fetching line items and updating the state.
 *
 * @param {LineItemsState} state - The current state.
 * @param {FetchLineItemsAction} action - The action to be handled.
 * @returns {LineItemsState} The new state with the fetched line items.
 */
export const handleFetchLineItems: Action<FetchLineItemsAction> = (
  state: LineItemsState,
  action?: FetchLineItemsAction,
): LineItemsState => {
  if (!action?.payload) return state;
  const { event, paginationEnabled, t } = action.payload;
  const { lots: originalLots } = event;
  const baseChanges = defaultChanges({
    isPriceUpdate: event.request_type === REQUEST_TYPE_PRICE_UPDATE,
    lots: originalLots,
    t,
  });

  // Preserve any unsaved new-lot entries from the previous state.changes so that
  // SHOULD_REFRESH (e.g. after an adder column auto-save) does not wipe lots the
  // buyer has added but not yet saved at the event level. New lots are identified
  // by the `new-<uuid>` id pattern — the server response will never include them.
  //
  // Skipped when the refresh follows a full successful save (handleSaveChanges
  // dispatches SHOULD_REFRESH with payload.preserveNewLots=false): that save
  // already persisted everything in state.changes, including any new lot, so
  // originalLots below already contains it under its real id. Re-merging the
  // stale `new-<uuid>` entry here would resurrect it as a second, permanently
  // "dirty" duplicate of the same lot (PEPPERMINT-2803).
  const preservedNewLots: Record<ID, LotChanges> = {};
  if (state.shouldPreserveNewLotsOnRefresh) {
    Object.entries(state.changes ?? {}).forEach(([lotId, lotChange]) => {
      if (matchesNew(String(lotId)) && !lotChange?._destroy) {
        preservedNewLots[lotId] = lotChange;
      }
    });
  }
  const changes = { ...baseChanges, ...preservedNewLots };
  const changedLots = applyChanges({ lotChanges: changes, lots: originalLots });

  return produce(state, (draft: LineItemsState): void => {
    draft.event = createEventState({ paginationEnabled, request: event });
    if (draft.event.paginationEnabled) {
      draft.lotsPagination = {};
      draft.shellRefreshCount = 0;
    }
    draft.refreshValidation = true;
    draft.lotsErrors = {} as Record<ID, ErrorState>;
    if (draft.event.paginationEnabled) idErrorsStore.clearAll();
    draft.isLoading = false;
    draft.lots = changedLots;
    draft.originalLots = originalLots;
    draft.changes = changes;
    draft.shouldRefresh = false;
    // Reset to the default (preserve) so this one-shot flag never leaks into the
    // next natural refresh cycle. A refresh that needs to suppress preservation
    // must explicitly opt in again via SHOULD_REFRESH's payload.
    draft.shouldPreserveNewLotsOnRefresh = true;
    draft.isInProgress = false;
    // `saveLineItemsAfterImport` is cleared by handleImportSaveFinished,
    // dispatched from handleSaveChanges' finally block — fires on both
    // success and failure paths so the next manual grid save can't inherit
    // mechanism='import' even when the import-driven save fails before
    // SHOULD_REFRESH is reached.

    // only trigger baseline fetching for new lot or replace all after line item import.
    if (state.baselineState.fetchBaselinesAfterSave) {
      draft.baselineState.fetchBaselines = true;
    }
  });
};

export const handleFinishProgress:
Action<FinishProgressAction> = (state: LineItemsState): LineItemsState => (
  produce(state, (draft: LineItemsState): void => {
    draft.isInProgress = false;
  })
);

// Clears the import-driven save flag. Dispatched from handleSaveChanges' finally
// block after the save attempt completes, success or failure. Single canonical
// clear point — replaces the previous clear inside handleFetchLineItems (which
// only ran on the SHOULD_REFRESH success path and left the flag stuck on save
// failure, causing the next manual grid save to inherit mechanism='import').
export const handleLineItemsImportSaveFinished: Action<LineItemsImportSaveFinishedAction> = (
  state: LineItemsState,
  action?: LineItemsImportSaveFinishedAction,
): LineItemsState => (
  produce(state, (draft: LineItemsState): void => {
    draft.importState.status.saveLineItemsAfterImport = false;

    // Only a SUCCEEDED save puts everything in `changes` on the server; left behind, those
    // `new-<uuid>` ids get re-materialised by `getNewLots` into a phantom lot (ARKIN-1483). On a
    // failed save they are the buyer's unsaved work, so clearing them would delete it.
    if (!action?.payload?.success) return;

    draft.changes = {};
    draft.lots = applyChanges({ lotChanges: draft.changes, lots: draft.originalLots });
  })
);

/**
 * Handles changes to multiple line items and/or custom columns items and updating the state.
 *
 * Note: This action is only for content changes (typing, editing values), not structural changes.
 * The changes are stored in draft.changes but draft.lots is not immediately updated to prevent
 * flickering during typing. The lots will be updated on save, page navigation, or other sync points.
 *
 * @param {LineItemsState} state - The current state.
 * @param {LineItemChangeAction} action - The action to be handled.
 * @returns {LineItemsState} The new state with the updated line item changes.
 */
export const handleLineItemChanges: Action<LineItemChangeAction> = (
  state: LineItemsState,
  action?: LineItemChangeAction,
): LineItemsState => {
  if (!action?.payload) return state;

  const { customColumnItems, lineItems, lotId, shouldApplyChanges } = action.payload;

  const edited = produce(state, (draft: LineItemsState): void => {
    ensureLotStructure(draft, lotId);
    processLineItemChanges(draft, lotId, lineItems);
    processCustomColumnItemChanges(draft, lotId, customColumnItems);
  });

  if (edited.event.paginationEnabled) return settleLotEdit(edited, lotId, { remerge: Boolean(shouldApplyChanges) });

  return produce(edited, (draft: LineItemsState): void => {
    draft.changes = cleanUpChanges(draft.changes);
    if (shouldApplyChanges) draft.lots = applyChanges({ lotChanges: draft.changes, lots: draft.originalLots });
  });
};

/**
 * Handles cancelling the import of line items and updates the state.
 *
 * @param state {LineItemsState}
 * @returns {LineItemsState} The new state with the import state updated.
 */
export const handleLineItemImportCancel: Action<LineItemsImportCancelAction> = (
  state: LineItemsState,
): LineItemsState => produce(state, (draft: LineItemsState): void => {
  draft.importState = {
    data: {
      ...defaultImportState.data
    },
    status: {
      ...draft.importState.status,
      ...defaultImportState.status,
      // Preserving this flag for replace all operations instead of resetting it
      // since we need to maintain its value for save operations after import
      // The modal closes after import confirmation
      saveLineItemsAfterImport: draft.importState.status.saveLineItemsAfterImport,
    },
  };
});

/**
 * Handles the confirmation of adding the imported line items to the existing and updates the state.
 *
 * @param state {LineItemsState}
 * @param _action {LineItemsImportConfirmAddAction}
 * @param request {EventRequest}
 * @returns {LineItemsState} The new state with the updated lots
 */
export const handleLineItemImportConfirmAdd: Action<LineItemsImportConfirmAddAction> = (
  state: LineItemsState,
  _action?: LineItemsImportConfirmAddAction,
  request?: EventRequest,
): LineItemsState => {
  // NSB: drop pick-sourced item resolutions before the rows rebuild, so a re-uploaded multi-match
  // name shows the picker again instead of re-applying the previous pick (and being auto-saved).
  resetPickedResolutions();
  // NSB: let the imported rows paint plain. The NSB cells are what make a large import slow to become
  // readable, so they wait until the match sweep finishes and the buyer asks for them.
  withholdNsbCells();
  // Restart the sweep so the Get offer comes back for the rows just added. Safe on an ADD: sweepItem
  // short-circuits any row that already carries a resolution, so the lots already there keep their
  // matches, manual entries and row-level choices.
  markUploadArrived();
  // Step 4a/4c fork: rows with a user-provided price (> 0) are marked Manual Entry so NSB skips them.
  markUserProvidedBaselines(state.importState.data.importedLots, request?.id);
  return processImportConfirmationResults({ isReplaceAll: false, request, state });
};

/**
 * Handles the confirmation of replacing all existing line items
 * with the imported ones and updates the state.
 *
 * @param state {LineItemsState}
 * @param action {LineItemsImportConfirmAddAction}
 * @param request {EventRequest}
 * @returns {LineItemsState} The new state with the updated lots
 */
export const handleLineItemImportConfirmReplaceAll:
Action<LineItemsImportConfirmReplaceAllAction> = (
  state: LineItemsState,
  action?: LineItemsImportConfirmReplaceAllAction,
  request?: EventRequest,
): LineItemsState => {
  if (!action?.payload) return state;
  const { deletedQualifyingCustomColumnHeaders } = action.payload;

  // NSB: drop pick-sourced item resolutions before the rows rebuild, so a re-uploaded multi-match
  // name shows the picker again instead of re-applying the previous pick (and being auto-saved).
  resetPickedResolutions();
  // NSB (cross-event fix): a replace-all rebuilds every row, so reset the full per-row line-item state —
  // resolved matches, applied prices, seed guards, badges, per-row config, and the by-name match/flavor
  // caches — as an event change does. Otherwise stale per-row state makes freshly-matched rows read
  // "Manual Entry". Event-level method selection and lot overrides are preserved (replace keeps the method).
  resetNsbLineItemState();
  // NSB: after that reset, withhold the cells so the rebuilt rows paint plain (see the new-lot path).
  // Order matters — resetNsbLineItemState puts them back to the default, which is installed.
  withholdNsbCells();
  // Put the sweep back to "nothing asked for yet" so the Get offer returns. AFTER resetNsbLineItemState,
  // which clears the rest of that store.
  markUploadArrived();
  // Step 4a/4c fork: rows with a user-provided price (> 0) are marked Manual Entry so NSB skips them.
  markUserProvidedBaselines(state.importState.data.importedLots, request?.id);
  return processImportConfirmationResults({
    deletedQualifyingCustomColumnHeaders, isReplaceAll: true, request, state,
  });
};

/**
 * Handles the error of importing line items and updates the state.
 *
 * @param state {LineItemsState}
 * @param action {LineItemsImportErrorAction}
 * @returns {LineItemsState} The new state with the import state updated.
 */
export const handleLineItemImportError: Action<LineItemsImportErrorAction> = (
  state: LineItemsState,
  action?: LineItemsImportErrorAction,
): LineItemsState => {
  if (!action?.payload) return state;
  const {
    disableSave,
    errorLotIndexes,
    imported,
    importedLots,
    lotErrorMap,
    showLineItemsModal,
  } = action.payload;

  return produce(state, (draft: LineItemsState): void => {
    draft.importState = {
      data: {
        ...draft.importState.data,
        ...defaultImportState.data,
        errorLotIndexes,
        importedLots,
        lotErrorMap,
      },
      status: {
        ...draft.importState.status,
        ...defaultImportState.status,
        disableSave,
        imported,
        showLineItemsModal,
      },
    };
  });
};

/**
 * Handles the start of importing line items and updates the state.
 *
 * @param state {LineItemsState}
 * @param action {LineItemsImportStartAction}
 * @returns {LineItemsState} The new state with the import state updated.
 */
export const handleLineItemImportStart: Action<LineItemsImportStartAction> = (
  state: LineItemsState,
  action?: LineItemsImportStartAction,
): LineItemsState => {
  if (!action?.payload) return state;
  const {
    disableSave,
    inProgress,
    lotErrorMap,
  } = action.payload;

  return produce(state, (draft: LineItemsState): void => {
    draft.baselineState.showBaselinePriceUpdateAlert = false;
    draft.importState = {
      data: { ...draft.importState.data, lotErrorMap },
      status: {
        ...draft.importState.status,
        disableSave,
        inProgress
      },
    };
  });
};

/**
 * Handles the success of importing line items and updates the state.
 *
 * @param state {LineItemsState}
 * @param action {LineItemsImportSuccessAction}
 * @returns {LineItemsState} The new state with the import state updated.
 */
export const handleLineItemImportSuccess: Action<LineItemsImportSuccessAction> = (
  state: LineItemsState,
  action?: LineItemsImportSuccessAction,
): LineItemsState => {
  if (!action?.payload) return state;
  const {
    disableSave,
    imported,
    importedLots,
    showLineItemsModal,
  } = action.payload;

  return produce(state, (draft: LineItemsState): void => {
    draft.importState = {
      data: {
        ...draft.importState.data,
        ...defaultImportState.data,
        importedLots,
        totalExistingLineItems: getTotalLineItemCount(draft?.lots),
        totalImportedLineItems: getTotalLineItemCount(importedLots),
      },
      status: {
        ...draft.importState.status,
        ...defaultImportState.status,
        disableSave,
        imported,
        showLineItemsModal,
        uploadStartedFromEmptyLot: draft.importState.status.uploadStartedFromEmptyLot,
        uploadStartedFromNewEvent: draft.importState.status.uploadStartedFromNewEvent,
      },
    };
  });
};

/**
 * Handles the upload of line items and sets showOneSchemaImportModal to true.
 *
 * @param state {LineItemsState}
 * @param action {LineItemsUploadAction}
 * @returns {LineItemsState} The new state with the import state
 * updated to showOneSchemaImportModal.
 */
export const handleLineItemsUpload: Action<LineItemsUploadAction> = (
  state: LineItemsState,
  action?: LineItemsUploadAction,
): LineItemsState => {
  if (!action?.payload) return state;
  const { uploadStartedFromEmptyLot, uploadStartedFromNewEvent } = action.payload;

  return produce(state, (draft: LineItemsState): void => {
    draft.importState = {
      ...draft.importState,
      status: {
        ...draft.importState.status,
        showOneSchemaImportModal: true,
        uploadStartedFromEmptyLot,
        uploadStartedFromNewEvent,
      },
    };
  });
};

/**
 * Applies the descriptive column limit to a file upload.
 *
 * @param state {LineItemsState}
 * @param action {LineItemsUploadApplyColumnLimitAction}
 * @returns {LineItemsState} The new state with the column limits applied
 */
export const handleApplyDescriptiveColumnLimit: Action<LineItemsUploadApplyColumnLimitAction> = (
  state: LineItemsState,
  action?: LineItemsUploadApplyColumnLimitAction
): LineItemsState => {
  if (!action?.payload) return state;

  const { maxColumnLimit } = action.payload;

  return produce(state, (draft: LineItemsState): void => {
    draft.importState = {
      ...draft.importState,
      data: {
        ...draft.importState.data,
        importedLots: applyDescriptiveColumnLimits(draft.importState.data.importedLots, maxColumnLimit)
      },
      status: {
        ...draft.importState.status,
        showDescriptiveColumnsWarningModal: false,
      },
    };
  });
};

/**
 * Handles the opening and closing of column limits modal.
 *
 * @param state {LineItemsState}
 * @param action {LineItemsUploadColumnLimitAction}
 * @returns {LineItemsState} The new state with the import state
 * updated to open/close the line items import modal.
 */
export const handleShowLineItemsColumnLimitModal: Action<LineItemsUploadColumnLimitAction> = (
  state: LineItemsState,
  action?: LineItemsUploadColumnLimitAction
): LineItemsState => {
  if (!action?.payload) return state;

  const { showModal } = action.payload;

  return produce(state, (draft: LineItemsState): void => {
    draft.importState = {
      ...draft.importState,
      status: {
        ...draft.importState.status,
        showDescriptiveColumnsWarningModal: showModal,
      },
    };
  });
};

/**
 * Handles cancelling the upload of line items.
 *
 * @param state {LineItemsState}
 * @returns {LineItemsState} The new state with the import state
 * updated to showOneSchemaImportModal set to false.
 */
export const handleLineItemsUploadCancel: Action<LineItemsUploadCancelAction> = (
  state: LineItemsState,
): LineItemsState => produce(state, (draft: LineItemsState): void => {
  draft.importState = {
    ...draft.importState,
    status: {
      ...draft.importState.status,
      showOneSchemaImportModal: false,
    },
  };
});

/**
 * Handles changes to the lot card header and updating the state.
 *
 * @param {LineItemsState} state - The current state.
 * @param {LotCardHeaderChangesAction} action - The action to be handled.
 * @returns {LineItemsState} The new state with the updated lot card header.
 */
export const handleLotCardHeaderChanges: Action<LotCardHeaderChangesAction> = (
  state: LineItemsState,
  action?: LotCardHeaderChangesAction,
): LineItemsState => {
  if (!action?.payload) return state;
  const { lotId, ...updates } = action.payload;
  return produce(state, (draft: LineItemsState): void => {
    draft.changes = draft.changes || ({} as Record<ID, LotChanges>);
    draft.changes[lotId] = updateChanges({
      changes: draft.changes[lotId] || ({ id: lotId } as LotChanges),
      original: getOriginalLot({ lotId, lots: draft.originalLots }),
      updates,
    }) as LotChanges;
    // IMPORTANT: This will check if the lot has changed, but it won't detect changes
    // if it's NOT listed on `details/utils/checkForChanges#lotFields`
    const isChanged = isLotChanged({ lotId, state: draft });

    if (!isChanged) {
      delete draft.changes[lotId];
    }
    draft.changes = cleanUpChanges(draft.changes);
    draft.lots = applyChanges({ lotChanges: draft.changes, lots: draft.originalLots });
  });
};

export const handleRefreshValidation: Action<RefreshValidationAction> = (
  state: LineItemsState,
): LineItemsState => produce(state, (draft: LineItemsState): void => {
  draft.refreshValidation = true;
  draft.lotsErrors = {} as Record<ID, ErrorState>;
  if (draft.event.paginationEnabled) idErrorsStore.clearAll();
});

/** Flag on, cell errors go to the id-errors store, so the grid reports the finished pass here instead of through ERRORS_CHANGE. */
export const handleRefreshValidationDone: Action<RefreshValidationDoneAction> = (
  state: LineItemsState,
): LineItemsState => produce(state, (draft: LineItemsState): void => {
  if (draft.refreshValidation) draft.refreshValidation = false;
});

export const handleRemoveColumn: Action<RemoveColumnAction> = (
  state: LineItemsState,
  action?: RemoveColumnAction,
): LineItemsState => {
  if (!action?.payload) return state;
  const {
    lotId, headerId: removeId, col,
  } = action.payload;

  const marked = produce(state, (draft: LineItemsState): void => {
    draft.changes = draft.changes || ({} as Record<ID, LotChanges>);
    draft.changes[lotId] = draft.changes[lotId] || ({ id: lotId } as LotChanges);
    draft.changes[lotId].custom_column_headers = draft.changes[lotId].custom_column_headers
      || ({} as Record<ID, CustomColumnHeader>);
    if (matchesNew(removeId)) {
      // If the column is new, just delete it from changes
      delete draft.changes[lotId].custom_column_headers[removeId];
    } else {
      // Mark it for destruction
      draft.changes[lotId].custom_column_headers[removeId] =
        draft.changes[lotId].custom_column_headers[removeId]
        || ({} as CustomColumnHeader);
      draft.changes[lotId].custom_column_headers[removeId] = {
        _destroy: true,
        id: removeId,
      } as CustomColumnHeader;
    }
    if (draft.event.paginationEnabled) {
      draft.changes[lotId] = dropPendingColumnCells(draft.changes[lotId], removeId);
    } else {
      draft.lots?.forEach((lot: Lot): void => {
        if (lot.id !== lotId) return;
        lot.line_items.forEach((lineItem: LineItem): void => {
          const itemId = lineItem.id;
          const customColumnItemId = lineItem?.custom_column_items?.find(
            (cci: CustomColumnItem): boolean => cci.custom_column_header_id === removeId,
          )?.id;

          if (!customColumnItemId) return;
          draft.changes[lotId].line_items = draft.changes[lotId].line_items
            || ({} as Record<ID, LineItemChanges>);
          draft.changes[lotId].line_items[itemId] = draft.changes[lotId].line_items[itemId]
            || ({} as LineItemChanges);
          draft.changes[lotId].line_items[itemId].custom_column_items =
            draft.changes[lotId].line_items[itemId].custom_column_items
            || ({} as Record<ID, CustomColumnItem>);
          delete draft.changes[lotId].line_items[itemId].custom_column_items[customColumnItemId];
        });
      });
    }

    // Remove the error state for the column
    if (col != null) {
      draft.lotsErrors = draft.lotsErrors || ({} as Record<ID, ErrorState>);
      draft.lotsErrors[lotId] = draft.lotsErrors[lotId] || ({} as ErrorState);
      let errors = { ...draft.lotsErrors[lotId] };
      Object.keys(errors).forEach(row => {
        errors = removeErrorState({ col, prev: errors, row });
      });
      draft.lotsErrors[lotId] = errors;
    }
    // By header id in every lot: an adder column shares its id across lots, and SYNC_ADDER_COLUMNS drops it there too.
    if (draft.event.paginationEnabled) idErrorsStore.clearColumn(String(removeId));
  });

  const removedHeader = getOriginalHeader({ headerId: removeId, lotId, lots: state.originalLots })
    ?? state.changes[lotId]?.custom_column_headers?.[removeId];
  const isAdder = removedHeader?.column_type === getAdderColumnType();
  if (marked.event.paginationEnabled && !isAdder) return settleLotEdit(marked, lotId, { remerge: true });

  return produce(marked, (draft: LineItemsState): void => {
    draft.changes = cleanUpChanges(draft.changes);
    draft.lots = applyChanges({ lotChanges: draft.changes, lots: draft.originalLots });
  });
};

export const handleErrorsChanges: Action<ErrorsChangesAction> = (
  state: LineItemsState,
  action?: ErrorsChangesAction,
): LineItemsState => {
  if (!action?.payload) return state;
  const {
    lotId, lineItemId, col, isValid,
  } = action.payload;

  return produce(state, (draft: LineItemsState): void => {
    draft.refreshValidation = false;
    draft.lotsErrors = draft.lotsErrors || ({} as Record<ID, ErrorState>);
    draft.lotsErrors[lotId] = draft.lotsErrors[lotId] || ({} as ErrorState);
    const prev = { ...draft.lotsErrors[lotId] };

    if (isValid) {
      // Remove the error if it is valid
      draft.lotsErrors[lotId] = removeErrorState({ col, prev, row: lineItemId });
    } else {
      // Add the error if it is not valid
      draft.lotsErrors[lotId] = addErrorState({ col, prev, row: lineItemId });
    }
  });
};

/**
 * Handles changes to the event and updating the state.
 *
 * @param {LineItemsState} state - The current state.
 * @param {EventChangesAction} action - The action to be handled.
 * @returns {LineItemsState} The new state with the updated event details.
 */
export const handleEventChanges: Action<EventChangesAction> = (
  state: LineItemsState,
  action?: EventChangesAction,
): LineItemsState => {
  if (!action?.payload) return state;
  const { request } = action.payload;

  return produce(state, (draft: LineItemsState): void => {
    // RequestProvider loads lots separately; its snapshot can lag behind this editor.
    draft.event = createEventState({
      paginationEnabled: draft.event.paginationEnabled,
      request: { ...request, lots: draft.originalLots },
    });
  });
};

export const handleEventIncumbentChange: Action<EventIncumbentChangeAction> = (
  state: LineItemsState,
  action?: EventIncumbentChangeAction,
): LineItemsState => {
  if (!action?.payload) return state;
  const { incumbentId } = action.payload;

  return produce(state, (draft: LineItemsState): void => {
    draft.event = {
      ...draft.event,
      eventIncumbentId: incumbentId,
    };

    draft.changes = draft.changes || ({} as Record<ID, LotChanges>);

    const lotIds = mergeLots(
      { lotChanges: draft.changes, lots: draft.originalLots },
    ).map((lot: Lot): ID => lot.id);

    // Update the incumbent_id for all lots
    lotIds.forEach((lotId: ID): void => {
      draft.changes[lotId] = draft.changes[lotId] || ({ id: lotId } as LotChanges);
      draft.changes[lotId] = {
        ...draft.changes[lotId],
        incumbent_id: incumbentId,
      } as LotChanges;
    });

    draft.changes = cleanUpChanges(draft.changes);
    draft.lots = applyChanges({ lotChanges: draft.changes, lots: draft.originalLots });
  });
};

/**
 * Handles refreshing the state.
 *
 * @param {LineItemsState} state - The current state.
 * @returns {LineItemsState} The new state with the refresh flag set.
 */
export const handleRefresh: Action<ShouldRefreshAction> = (
  state: LineItemsState,
  action?: ShouldRefreshAction,
): LineItemsState => produce(state, (draft: LineItemsState): void => {
  draft.shouldRefresh = true;
  draft.shouldPreserveNewLotsOnRefresh = action?.payload?.preserveNewLots ?? true;
});

export const handleStartFetch: Action<StartFetchAction> = (
  state: LineItemsState,
): LineItemsState => produce(state, (draft: LineItemsState): void => {
  draft.isLoading = true;
});

export const handleStartProgress:
Action<StartProgressAction> = (state: LineItemsState):
LineItemsState => produce(state, (draft: LineItemsState): void => {
  draft.isInProgress = true;
});

export const handleUpdateUIConfig: Action<UpdateUiConfigAction> = (
  state: LineItemsState,
  action?: UpdateUiConfigAction,
): LineItemsState => {
  if (!action?.payload) return state;
  const { uiConfig } = action.payload;

  return produce(state, (draft: LineItemsState): void => {
    Object.keys(uiConfig).forEach((key: keyof GlobalLotsUiConfigKeys): void => {
      const eventKey = globalLotsUiConfigKeys[key];

      if (!eventKey) return;
      draft.event = { ...draft.event, [eventKey]: uiConfig[key] };
    });
  });
};

/**
 * Syncs adder column headers from the source lot to all other lots.
 * Used after adder column API operations to ensure immediate UI consistency
 * across all lots, since the SHOULD_REFRESH → useEffect reload chain can
 * be unreliable due to React batching and the filteredLots indirection.
 */
export const handleSyncAdderColumns: Action<SyncAdderColumnsAction> = (
  state: LineItemsState,
  action?: SyncAdderColumnsAction,
): LineItemsState => {
  if (!action?.payload) return state;
  const { sourceLotId } = action.payload;

  return produce(state, (draft: LineItemsState): void => {
    const adderType = getAdderColumnType();
    const sourceLot = draft.lots?.find(l => l.id === sourceLotId);

    if (!sourceLot) return;

    // Only sync event-wide adders (lot_ids is null/empty); lot-scoped adders stay on their lot
    const sourceAdderHeaders = sourceLot.custom_column_headers?.filter(
      h => h.column_type === adderType && !h._destroy && !h.adder_column_config?.lot_ids?.length,
    ) ?? [];

    draft.changes = draft.changes || ({} as Record<ID, LotChanges>);

    draft.lots?.forEach(lot => {
      if (lot.id === sourceLotId) return;

      draft.changes[lot.id] = draft.changes[lot.id] || ({ id: lot.id } as LotChanges);
      draft.changes[lot.id].custom_column_headers =
        draft.changes[lot.id].custom_column_headers || ({} as Record<ID, CustomColumnHeader>);

      // Remove existing event-wide adder headers from changes; preserve lot-scoped ones
      Object.keys(draft.changes[lot.id].custom_column_headers).forEach(key => {
        const h = draft.changes[lot.id].custom_column_headers[key as ID];
        if (h?.column_type === adderType && !h?.adder_column_config?.lot_ids?.length) {
          delete draft.changes[lot.id].custom_column_headers[key as ID];
        }
      });

      sourceAdderHeaders.forEach(h => {
        draft.changes[lot.id].custom_column_headers[h.id] = {
          ...h,
          lot_id: lot.id,
        } as CustomColumnHeader;
      });
    });

    draft.changes = cleanUpChanges(draft.changes);
    draft.lots = applyChanges({ lotChanges: draft.changes, lots: draft.originalLots });
  });
};

/**
 * Flag on. Drops a response for a page or size the lot no longer requests. An identical (cached) page
 * still counts as an arrival and increments `loads`.
 */
export const handleLotPageLoaded: Action<LotPageLoadedAction> = (
  state: LineItemsState,
  action?: LotPageLoadedAction,
): LineItemsState => {
  if (!action?.payload) return state;
  const { lotId, page, pageIds, pageSize, rows, total } = action.payload;
  const lotIndex = state.originalLots.findIndex(lot => String(lot.id) === String(lotId));
  if (lotIndex === -1) return state;

  const pagination = getLotPagination(state, lotId);
  if (page !== pagination.currentPage || pageSize !== pagination.pageSize) return state;

  const lotsPagination = {
    ...state.lotsPagination,
    [lotId]: { ...pagination, loadedPage: page, loadedPageSize: pageSize, loads: pagination.loads + 1, pageIds, total },
  };
  const originalLot = state.originalLots[lotIndex];
  if (originalLot.line_items === rows) return { ...state, lotsPagination };

  const originalLots = [...state.originalLots];
  originalLots[lotIndex] = { ...originalLot, line_items: rows };
  const walked = fillPendingColumnCells({ changes: state.changes, lot: originalLot, rows });
  const changes = walked === state.changes ? walked : cleanUpLotChanges(walked, lotId);

  return {
    ...state,
    changes,
    lots: remergeLot({ changes, lotId, lots: state.lots, originalLots }),
    lotsPagination,
    originalLots,
  };
};

/** Flag on: a bigger page size that leaves the current page past the end moves the lot back to the new last page. */
export const handleLotPageChange: Action<LotPageChangeAction> = (
  state: LineItemsState,
  action?: LotPageChangeAction,
): LineItemsState => {
  if (!action?.payload) return state;
  const { lotId, page, pageSize } = action.payload;
  const pagination = getLotPagination(state, lotId);
  const lastPage = Math.max(1, Math.ceil(pagination.total / pageSize));
  const currentPage = pageSize === pagination.pageSize ? page : Math.min(page, lastPage);
  if (currentPage === pagination.currentPage && pageSize === pagination.pageSize) return state;

  return { ...state, lotsPagination: { ...state.lotsPagination, [lotId]: { ...pagination, currentPage, pageSize } } };
};

/**
 * Flag on, every refresh after the first load. Keeps each lot's rows, page, cell errors and overlay. Bumps
 * shellRefreshCount (which invalidates the page queries) when the refresh brought an event, or when a failed
 * refresh follows a successful save.
 */
export const handleEventShellRefreshed: Action<EventShellRefreshedAction> = (
  state: LineItemsState,
  action?: EventShellRefreshedAction,
): LineItemsState => {
  const event = action?.payload?.event;
  const followsSave = !state.shouldPreserveNewLotsOnRefresh;
  const settled = produce(state, (draft: LineItemsState): void => {
    draft.isInProgress = false;
    draft.isLoading = false;
    draft.shouldPreserveNewLotsOnRefresh = true;
    draft.shouldRefresh = false;
    if (event || followsSave) draft.shellRefreshCount += 1;
  });
  if (!event) return settled;

  const { lots, originalLots } = mergeShellLots({
    changes: state.changes,
    lots: state.lots,
    originalLots: state.originalLots,
    shellLots: event.lots ?? [],
  });

  return {
    ...settled,
    event: createEventState({ paginationEnabled: state.event.paginationEnabled, request: event }),
    lots,
    originalLots,
  };
};

export const actions = {
  [ACCEPTED_CURRENCIES_CHANGE]: handleAcceptedCurrenciesChange,
  [ADD_BLANK_LINE_ITEM]: handleAddBlankLineItem,
  [ADD_CUSTOM_COLUMN]: handleAddCustomColumn,
  [ADD_LOT]: handleAddLot,
  [APPLY_BASELINE_CHANGES]: handleApplyBaselineChanges,
  [APPLY_CHANGES_TO_CUSTOM_COLUMN]: handleApplyChangesToCustomColumn,
  [COLLAPSE_LOT]: handleCollapseLot,
  [CURRENCY_CHANGE]: handleCurrencyChange,
  [DELETE_LINE_ITEM]: handleDeleteLineItem,
  [DELETE_LOT]: handleDeleteLot,
  [DISCARD_CHANGES]: handleDiscardChanges,
  [ERRORS_CHANGE]: handleErrorsChanges,
  [EVENT_CHANGES]: handleEventChanges,
  [EVENT_INCUMBENT_CHANGE]: handleEventIncumbentChange,
  [EVENT_SHELL_REFRESHED]: handleEventShellRefreshed,
  [EXPAND_ALL]: handleExpandAll,
  [EXPAND_LOT]: handleExpandLot,
  [FETCH_BASELINE_IN_PROGRESS]: handleBaselineFetchProgress,
  [FETCH_BASELINE_IN_PROGRESS_END]: handleBaselineFetchProgressEnd,
  [FETCH_BASELINE_PRICES]: handleFetchBaselinesAfterSave,
  [FETCH_LINE_ITEMS]: handleFetchLineItems,
  [FINISH_PROGRESS]: handleFinishProgress,
  [LINE_ITEM_CHANGE]: handleLineItemChanges,
  [LINE_ITEMS_IMPORT_CANCEL]: handleLineItemImportCancel,
  [LINE_ITEMS_IMPORT_CONFIRM_ADD]: handleLineItemImportConfirmAdd,
  [LINE_ITEMS_IMPORT_CONFIRM_REPLACE_ALL]: handleLineItemImportConfirmReplaceAll,
  [LINE_ITEMS_IMPORT_ERROR]: handleLineItemImportError,
  [LINE_ITEMS_IMPORT_SAVE_FINISHED]: handleLineItemsImportSaveFinished,
  [LINE_ITEMS_IMPORT_START]: handleLineItemImportStart,
  [LINE_ITEMS_IMPORT_SUCCESS]: handleLineItemImportSuccess,
  [LINE_ITEMS_UPLOAD]: handleLineItemsUpload,
  [LINE_ITEMS_UPLOAD_APPLY_COLUMN_LIMIT]: handleApplyDescriptiveColumnLimit,
  [LINE_ITEMS_UPLOAD_CANCEL]: handleLineItemsUploadCancel,
  [LINE_ITEMS_UPLOAD_COLUMN_WARNING]: handleShowLineItemsColumnLimitModal,
  [LOT_CARD_HEADER_CHANGES]: handleLotCardHeaderChanges,
  [LOT_PAGE_CHANGE]: handleLotPageChange,
  [LOT_PAGE_LOADED]: handleLotPageLoaded,
  [REFRESH_VALIDATION]: handleRefreshValidation,
  [REFRESH_VALIDATION_DONE]: handleRefreshValidationDone,
  [REMOVE_COLUMN]: handleRemoveColumn,
  [RESET_BASELINE_STATE]: handleResetBaselineState,
  [SHOULD_REFRESH]: handleRefresh,
  [SHOULD_RERENDER_LINE_ITEM_DATA]: handleRerenderTableData,
  [START_FETCH]: handleStartFetch,
  [START_PROGRESS]: handleStartProgress,
  [SYNC_ADDER_COLUMNS]: handleSyncAdderColumns,
  [UPDATE_UI_CONFIG]: handleUpdateUIConfig,
  [UPDATE_ZERO_PRICED_LINE_ITEMS]: handleUpdateZeroPricedLineItemCount
};
